import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Language, supportedLanguages } from '../hooks/useLanguage';

interface LanguageContextType {
    currentLanguage: Language;
    supportedLanguages: Language[];
    changeLanguage: (languageCode: string) => Promise<boolean>;
    isReady: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
    children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const { i18n } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
        // Get initial language from localStorage or default
        const saved = localStorage.getItem('i18nextLng');
        return supportedLanguages.find(lang => lang.code === saved) || supportedLanguages[0];
    });
    const [isReady, setIsReady] = useState(false);

    // Initialize language system
    useEffect(() => {
        const initializeLanguage = async () => {
            try {
                // Wait for i18n to be ready
                if (!i18n.isInitialized) {
                    await new Promise(resolve => {
                        i18n.on('initialized', resolve);
                    });
                }

                // Set current language based on i18n current language
                const currentLang = supportedLanguages.find(lang => lang.code === i18n.language);
                if (currentLang) {
                    setCurrentLanguage(currentLang);
                }

                setIsReady(true);
            } catch (error) {
                console.error('Failed to initialize language system:', error);
                setIsReady(true); // Still set ready to avoid blocking the app
            }
        };

        initializeLanguage();
    }, [i18n]);

    // Listen to language changes
    useEffect(() => {
        const handleLanguageChange = (lng: string) => {
            const newLanguage = supportedLanguages.find(lang => lang.code === lng);
            if (newLanguage) {
                setCurrentLanguage(newLanguage);
                // Ensure localStorage is always updated
                localStorage.setItem('i18nextLng', lng);
                localStorage.setItem('language', lng);
            }
        };

        i18n.on('languageChanged', handleLanguageChange);

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [i18n]);

    const changeLanguage = async (languageCode: string): Promise<boolean> => {
        try {
            const targetLanguage = supportedLanguages.find(lang => lang.code === languageCode);
            if (!targetLanguage) {
                console.error(`Unsupported language: ${languageCode}`);
                return false;
            }

            // Change language using i18next
            await i18n.changeLanguage(languageCode);

            // Update localStorage (redundant but ensures consistency)
            localStorage.setItem('i18nextLng', languageCode);
            localStorage.setItem('language', languageCode);

            // Update state
            setCurrentLanguage(targetLanguage);

            // Dispatch custom event for other parts of the app
            window.dispatchEvent(new CustomEvent('languageChanged', {
                detail: { language: languageCode, languageObject: targetLanguage }
            }));

            return true;
        } catch (error) {
            console.error('Failed to change language:', error);
            return false;
        }
    };

    const contextValue: LanguageContextType = {
        currentLanguage,
        supportedLanguages,
        changeLanguage,
        isReady,
    };

    return (
        <LanguageContext.Provider value={contextValue}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguageContext = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguageContext must be used within a LanguageProvider');
    }
    return context;
};

export default LanguageContext; 